/**
 * "Open in CarBuy app" helper for shared car links.
 *
 * This shows a small dismissible bar in mobile browsers. It punches out into
 * the app:
 *   - Android: an intent:// URL naming the package (works even inside Custom
 *     Tabs; bypasses the App Links auto-verify gate), with a Play Store fallback.
 *   - iOS: a claimed HTTPS Universal Link (`/get/app/car/{id}`) on the
 *     alternate CarBuy host. If the app is not installed, Safari lands on the
 *     install/web fallback page instead of showing the native "address invalid"
 *     alert that custom schemes trigger.
 *
 * Config is read from private carbuy-* <meta> tags emitted by
 * partials/mobile-app-meta.php. We intentionally avoid apple-itunes-app,
 * google-play-app, and al:* tags so browsers do not create their own top
 * "OPEN" UI.
 */
(function () {
  'use strict';

  var ua = navigator.userAgent || navigator.vendor || '';
  var isAndroid = /Android/i.test(ua);
  var isIOS =
    /iPhone|iPad|iPod/i.test(ua) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  if (!isAndroid && !isIOS) return; // desktop: native banners / no app

  var DISMISS_KEY = 'carbuy_open_in_app_dismissed';
  try {
    if (window.sessionStorage && sessionStorage.getItem(DISMISS_KEY) === '1') {
      return;
    }
  } catch (e) {
    /* storage may be blocked in private mode — show the bar anyway */
  }

  function meta(prop) {
    var el = document.querySelector('meta[property="' + prop + '"]');
    return el ? (el.getAttribute('content') || '') : '';
  }

  function metaName(name) {
    var el = document.querySelector('meta[name="' + name + '"]');
    return el ? (el.getAttribute('content') || '') : '';
  }

  var pkg = metaName('carbuy-android-package') || meta('al:android:package') || 'lv.carbuy.mobile';
  var iosAppId = metaName('carbuy-ios-app-id') || meta('al:ios:app_store_id');
  var webUrl =
    metaName('carbuy-app-url') || meta('al:ios:url') || meta('al:android:url') || window.location.href;
  var baseUrl;
  try {
    baseUrl = new URL(webUrl, window.location.origin);
  } catch (e) {
    baseUrl = new URL(window.location.href, window.location.origin);
  }

  // Canonical car slugs end in "-<id>"; pull the id so we can build direct
  // carbuy://car/<id> scheme links (and keep compact https short links working).
  var carId = '';
  try {
    var path = new URL(webUrl, window.location.origin).pathname;
    var m = /-(\d+)\/?$/.exec(path);
    if (m) carId = m[1];
  } catch (e) {
    /* ignore */
  }
  if (!carId) {
    var hook = document.querySelector('[data-car-id]');
    if (hook) carId = hook.getAttribute('data-car-id') || '';
  }

  function isCarBuyHost(hostname) {
    var host = (hostname || '').toLowerCase();
    return host === 'carbuy.lv' || host === 'www.carbuy.lv';
  }

  // Build the iOS handoff as an HTTPS Universal Link. The AASA-claimed
  // `/get/app/car/<id>` path opens the app when installed; without the app it
  // serves a normal install/web fallback page, avoiding iOS' scary custom-scheme
  // "address invalid" alert.
  function buildIosOpenUrl() {
    if (carId) {
      var carUrl = new URL(baseUrl.toString());
      carUrl.protocol = 'https:';
      if (isCarBuyHost(carUrl.hostname)) {
        carUrl.hostname = carUrl.hostname.toLowerCase() === 'www.carbuy.lv'
          ? 'carbuy.lv'
          : 'www.carbuy.lv';
      } else {
        carUrl.hostname = 'carbuy.lv';
      }
      carUrl.pathname = '/get/app/car/' + encodeURIComponent(carId);
      carUrl.search = '';
      carUrl.hash = '';
      return carUrl.toString();
    }
    // Fallback to the generic get-the-app flow if we couldn't determine an id.
    if (!baseUrl) return '';
    var url = new URL(baseUrl.toString());
    if (isCarBuyHost(url.hostname)) {
      url.hostname = url.hostname.toLowerCase() === 'www.carbuy.lv'
        ? 'carbuy.lv'
        : 'www.carbuy.lv';
    }
    url.pathname = '/get/go/ios';
    url.search = '';
    url.hash = '';
    return url.toString();
  }

  function normalizeIosOpenTarget(target) {
    var raw = String(target || '').trim();
    if (!raw || /^carbuy:\/\//i.test(raw)) {
      return buildIosOpenUrl();
    }
    try {
      var url = new URL(raw, window.location.origin);
      if (carId && isCarBuyHost(url.hostname) && /^\/get\/app\/car\//.test(url.pathname)) {
        return buildIosOpenUrl();
      }
    } catch (e) {
      /* keep the original target below */
    }
    return raw;
  }

  var labels = {
    lv: { text: 'Skatīt šo auto CarBuy lietotnē', open: 'Atvērt', install: 'Instalēt', close: 'Aizvērt' },
    ru: { text: 'Открыть это авто в приложении CarBuy', open: 'Открыть', install: 'Установить', close: 'Закрыть' },
    en: { text: 'Open this car in the CarBuy app', open: 'Open', install: 'Install', close: 'Close' }
  };
  var lang = (document.documentElement.lang || 'lv').slice(0, 2).toLowerCase();
  var L = labels[lang] || labels.lv;

  function diag(stage, data) {
    var payload = {
      stage: stage,
      platform: isIOS ? 'ios_web' : (isAndroid ? 'android_web' : 'web'),
      raw_url: data && data.rawUrl ? data.rawUrl : (webUrl || window.location.href),
      car_id: carId ? parseInt(carId, 10) || 0 : 0,
      message: data && data.message ? data.message : '',
      extra: {
        source: 'website_open_in_app',
        user_agent: ua,
        page_url: window.location.href,
        visibility_state: document.visibilityState || '',
        target_url: data && data.targetUrl ? data.targetUrl : '',
        fallback_url: data && data.fallbackUrl ? data.fallbackUrl : '',
        trigger: data && data.trigger ? data.trigger : '',
        did_leave: data && typeof data.didLeave === 'boolean' ? data.didLeave : null
      }
    };
    try {
      var json = JSON.stringify(payload);
      if (navigator.sendBeacon) {
        var blob = new Blob([json], { type: 'application/json' });
        if (navigator.sendBeacon('/api/mobile/app/deep-link-log', blob)) return;
      }
      fetch('/api/mobile/app/deep-link-log', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        keepalive: true,
        body: json
      }).catch(function () {});
    } catch (e) {
      /* diagnostics must never break navigation */
    }
  }

  diag('web_open_bar_loaded', { targetUrl: webUrl });

  function showIosInstallState(appStore) {
    if (!openBtn || !appStore) return;
    openBtn.href = appStore;
    openBtn.textContent = L.install;
    openBtn.setAttribute('data-ios-install-link', '1');
  }

  function openIosWithFallback(target, triggerName) {
    var appStore = iosAppId ? 'https://apps.apple.com/app/id' + iosAppId : webUrl;
    var openTarget = normalizeIosOpenTarget(target) || appStore;
    var didLeave = false;
    var fallbackTimer = null;

    function cancelFallback() {
      didLeave = true;
      diag('web_ios_open_cancel_fallback', {
        targetUrl: openTarget,
        fallbackUrl: appStore,
        trigger: triggerName || '',
        didLeave: true
      });
      if (fallbackTimer) {
        clearTimeout(fallbackTimer);
        fallbackTimer = null;
      }
      window.removeEventListener('pagehide', cancelFallback);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    }

    function onVisibilityChange() {
      if (document.hidden) {
        diag('web_ios_visibility_hidden', {
          targetUrl: openTarget,
          fallbackUrl: appStore,
          trigger: triggerName || ''
        });
        cancelFallback();
      }
    }

    window.addEventListener('pagehide', cancelFallback);
    document.addEventListener('visibilitychange', onVisibilityChange);

    diag('web_ios_open_attempt', {
      targetUrl: openTarget,
      fallbackUrl: appStore,
      trigger: triggerName || ''
    });

    fallbackTimer = setTimeout(function () {
      if (!didLeave && appStore) {
        diag('web_ios_fallback_show_install', {
          targetUrl: openTarget,
          fallbackUrl: appStore,
          trigger: triggerName || '',
          didLeave: false,
          message: 'Safari stayed visible after iOS app handoff; showing install link instead of auto-redirect.'
        });
        showIosInstallState(appStore);
      }
    }, 2800);

    window.location.href = openTarget;
  }

  function openInApp(triggerName) {
    if (isAndroid) {
      var noScheme = webUrl.replace(/^https?:\/\//i, '');
      var playStore =
        'https://play.google.com/store/apps/details?id=' +
        encodeURIComponent(pkg);
      var intentUrl =
        'intent://' +
        noScheme +
        '#Intent;scheme=https;package=' +
        pkg +
        ';S.browser_fallback_url=' +
        encodeURIComponent(playStore) +
        ';end';
      diag('web_android_intent_attempt', {
        targetUrl: intentUrl,
        fallbackUrl: playStore,
        trigger: triggerName || ''
      });
      // intent:// with an explicit package opens the installed app directly
      // (https App Link target), falling back to the Play Store if absent.
      window.location.href = intentUrl;
      return;
    }
    openIosWithFallback(buildIosOpenUrl(), triggerName || 'bar');
  }

  // Explicit triggers on page CTAs (e.g. "Offer price in app").
  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || typeof target.closest !== 'function') return;
    var trigger = target.closest('[data-open-in-app]');
    if (!trigger) return;
    if (isIOS) {
      // For iOS (including Chrome/FxiOS etc. which do not support https
      // Universal Links), we use the carbuy:// scheme on the anchor.
      // Explicitly drive the navigation from the click handler so it works
      // reliably even in third-party browsers/webviews. This is a user
      // gesture so iOS allows the scheme launch.
      event.preventDefault();
      var href = trigger.getAttribute('href') || '';
      openIosWithFallback(href || buildIosOpenUrl(), trigger.getAttribute('data-open-in-app-context') || 'cta');
      return;
    }
    event.preventDefault();
    openInApp(trigger.getAttribute('data-open-in-app-context') || 'cta');
  });

  function dismiss() {
    try {
      if (window.sessionStorage) sessionStorage.setItem(DISMISS_KEY, '1');
    } catch (e) {
      /* ignore */
    }
    diag('web_open_bar_dismissed', { targetUrl: webUrl });
    if (bar && bar.parentNode) bar.parentNode.removeChild(bar);
  }

  var bar = document.createElement('div');
  bar.setAttribute('role', 'region');
  bar.setAttribute('aria-label', L.text);
  bar.style.cssText = [
    'position:fixed',
    'left:0',
    'right:0',
    'bottom:0',
    'z-index:2147483000',
    'display:flex',
    'align-items:center',
    'gap:12px',
    'padding:10px 14px',
    'background:#ffffff',
    'box-shadow:0 -2px 12px rgba(0,0,0,0.18)',
    'font:14px/1.3 -apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Arial,sans-serif',
    'color:#111'
  ].join(';');

  var text = document.createElement('span');
  text.textContent = L.text;
  text.style.cssText = 'flex:1;min-width:0';

  var openBtn = document.createElement(isIOS ? 'a' : 'button');
  if (!isIOS) {
    openBtn.type = 'button';
  } else {
    // For iOS, use the claimed HTTPS handoff route so phones without the app
    // see the install page instead of a native custom-scheme error alert.
    var iosTarget = buildIosOpenUrl();
    var iosAppStoreHref = iosAppId ? 'https://apps.apple.com/app/id' + iosAppId : webUrl;
    openBtn.href = iosTarget || iosAppStoreHref;
    openBtn.setAttribute('rel', 'nofollow');
  }
  openBtn.textContent = L.open;
  openBtn.style.cssText = [
    'flex:0 0 auto',
    'border:0',
    'border-radius:8px',
    'padding:9px 16px',
    'background:#111',
    'color:#fff',
    'font-weight:600',
    'text-decoration:none',
    'cursor:pointer'
  ].join(';');
  if (!isIOS) {
    openBtn.addEventListener('click', function () {
      openInApp('bar');
    });
  } else {
    // For iOS third-party browsers (Chrome etc.) and webviews, the scheme
    // launch from the bottom bar benefits from an explicit gesture-driven
    // location change in addition to the href on the <a>.
    openBtn.addEventListener('click', function (e) {
      var href = openBtn.getAttribute('href') || '';
      if (openBtn.getAttribute('data-ios-install-link') === '1') {
        diag('web_ios_install_link_click', {
          targetUrl: href,
          fallbackUrl: href,
          trigger: 'bar_install'
        });
        return;
      }
      if (href) {
        // Prevent any default that might try to treat it as a normal link
        // and force the scheme (or fallback) immediately.
        e.preventDefault();
        openIosWithFallback(href, 'bar');
      }
    });
  }

  var closeBtn = document.createElement('button');
  closeBtn.type = 'button';
  closeBtn.setAttribute('aria-label', L.close);
  closeBtn.textContent = '\u00D7';
  closeBtn.style.cssText = [
    'flex:0 0 auto',
    'border:0',
    'background:transparent',
    'color:#666',
    'font-size:22px',
    'line-height:1',
    'padding:4px 6px',
    'cursor:pointer'
  ].join(';');
  closeBtn.addEventListener('click', dismiss);

  bar.appendChild(text);
  bar.appendChild(openBtn);
  bar.appendChild(closeBtn);

  function mount() {
    document.body.appendChild(bar);
    diag('web_open_bar_mounted', { targetUrl: webUrl });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mount);
  } else {
    mount();
  }
})();
